import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { ChevronsUpDown, LogOut, UserRound } from "lucide-react";

import { useAppSignOut } from "@/hooks/use-app-sign-out";
import { buildAccountProfileSettingsPath } from "@/lib/app-route-manifest";
import { getUserDisplayName, getUserInitials, type User } from "@/lib/user";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@runcheetah/ui";

export function SidebarProfileMenu({
  user,
  brandId,
  collapsed = false,
}: {
  user: User;
  brandId: string | undefined;
  collapsed?: boolean;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const handleSignOut = useAppSignOut();
  const displayName = getUserDisplayName(user);
  const initials = getUserInitials(user);
  const profilePath = buildAccountProfileSettingsPath(brandId);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={menuRef}
      className="relative"
      onBlur={(event) => {
        const nextFocus = event.relatedTarget;
        if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
          return;
        }

        setOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
          collapsed && "justify-center px-2",
          open
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.formatMessage({
          id: "sidebar.profileMenu.open",
          defaultMessage: "Open profile menu",
        })}
        title={displayName}
      >
        <Avatar className="h-8 w-8 rounded-lg">
          {user.image ? (
            <AvatarImage src={user.image} alt={displayName} referrerPolicy="no-referrer" />
          ) : null}
          <AvatarFallback className="rounded-lg bg-primary text-xs font-semibold text-primary-foreground">
            {initials}
          </AvatarFallback>
        </Avatar>
        {collapsed ? null : (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold leading-5 text-foreground">
                {displayName}
              </span>
              <span className="block truncate text-xs leading-4 text-muted-foreground">
                {user.email}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </>
        )}
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute bottom-full z-50 mb-2 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-deep",
            collapsed ? "left-full ml-2 w-64" : "left-0 right-0",
          )}
        >
          <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              {user.image ? (
                <AvatarImage src={user.image} alt={displayName} referrerPolicy="no-referrer" />
              ) : null}
              <AvatarFallback className="rounded-lg bg-primary text-xs font-semibold text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">
                {displayName}
              </span>
              <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
            </div>
          </div>
          <div className="my-1 h-px bg-border" />
          <Link
            to={profilePath}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:bg-muted/70 focus-visible:text-foreground"
          >
            <UserRound className="size-4" aria-hidden="true" />
            <FormattedMessage id="sidebar.profile" defaultMessage="Profile" />
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void handleSignOut();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-destructive outline-none transition-colors hover:bg-destructive/10 focus-visible:bg-destructive/10"
          >
            <LogOut className="size-4" aria-hidden="true" />
            <FormattedMessage id="sidebar.signOut" defaultMessage="Sign out" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
